/**
 * product-kpis-emitter.ts
 *
 * Singleton do KpiEmitter para o lado frontend.
 *
 * Regras:
 *  - Nenhum call-site importa SDK de analytics diretamente.
 *  - Em dev/test usa InMemoryKpiEmitter (inspecionavel em testes).
 *  - Em producao (import.meta.env.PROD) usa StubAnalyticsKpiEmitter ate
 *    que o ADR de observabilidade escolha o provedor final (Segment /
 *    Mixpanel / Amplitude). Divida tecnica marcada abaixo.
 *  - Erros de emissao sao absorvidos — nunca devem derrubar a UI.
 *
 * Divida tecnica D-FE-001:
 *   StubAnalyticsKpiEmitter so loga em console.debug. Quando o ADR de
 *   observabilidade fechar o provedor, substituir pela implementacao real
 *   aqui. Estimativa: 1 dia de trabalho.
 */

import {
  InMemoryKpiEmitter,
  StubAnalyticsKpiEmitter,
  type KpiEmitter,
  type KpiEvent,
} from '@mktvibe/kpis';

function buildEmitter(): KpiEmitter {
  if (import.meta.env.PROD) {
    return new StubAnalyticsKpiEmitter();
  }
  return new InMemoryKpiEmitter();
}

/** Singleton — toda a app usa esta instancia. */
export const kpiEmitter: KpiEmitter = buildEmitter();

/**
 * Emite um evento de KPI absorvendo erros de transporte.
 * Use esta funcao nos call-sites em vez de chamar kpiEmitter.emit diretamente
 * para garantir que o log de erro seja uniforme.
 */
export async function emitKpi(event: KpiEvent): Promise<void> {
  try {
    await kpiEmitter.emit(event);
  } catch (err) {
    // Erro de transporte nunca derruba a UI — apenas loga.
    console.warn('[kpi-emitter] falha ao emitir evento:', event.type, err);
  }
}

/**
 * Helper: retorna now() em ISO-8601 UTC.
 * Centraliza a geracao de timestamps para facilitar mock em testes.
 */
export function kpiNow(): string {
  return new Date().toISOString();
}
